
import React from 'react';
import { HashRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { LanguageProvider, useLanguage } from './LanguageContext';
import { CONTENT } from './content';
import Home from './pages/Home';
import Physicians from './pages/Physicians';
import Economics from './pages/Economics';
import VBCRealityCheck from './pages/VBCRealityCheck';
import Onboarding from './pages/Onboarding';
import Contact from './pages/Contact';
import Resources from './pages/Resources';
import Legal from './pages/Legal';

// Components
import VoiceAIModal from './components/VoiceAIModal';
import CuriousStrip from './components/CuriousStrip';

const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    // Don't scroll to top if going to legal section with anchor param
    if (!window.location.hash.includes('?section=')) {
        window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
};

const Header = () => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { language, toggleLanguage } = useLanguage();
  const t = CONTENT[language];

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm transition-all">
      <div className="container mx-auto px-4 lg:px-12 h-20 flex items-center justify-between">
        {/* Custom Logo */}
        <Link to="/" className="flex items-center gap-3 group" onClick={() => setIsOpen(false)}>
          <div className="w-10 h-10 relative transition-transform transform group-hover:scale-105 flex-shrink-0">
             <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full drop-shadow-sm">
              <circle cx="50" cy="50" r="50" fill="#D2E6E7" className="group-hover:fill-[#C1DADC] transition-colors"/>
              <circle cx="50" cy="42" r="18" fill="#D9924D" />
              <path d="M15 65C15 65 30 55 50 55C70 55 85 65 85 65V75C85 75 70 82 50 82C30 82 15 75 15 75V65Z" fill="#2F6B77" />
            </svg>
          </div>
          <div className="flex flex-col justify-center">
            <div className="flex items-baseline leading-none">
              <span className="text-2xl font-extrabold text-suncoast-primary tracking-tight">SUNCOAST</span>
              <span className="text-2xl font-extrabold text-suncoast-accent ml-1">MD</span>
              <span className="text-2xl font-extrabold text-suncoast-primary ml-1">PARTNERS</span>
            </div>
            <span className="hidden sm:block text-xs font-extrabold text-suncoast-accent tracking-wide mt-1">Local Support. Multi-Carrier Experience.</span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-6">
          {t.nav.map((item) => (
            <Link 
              key={item.path} 
              to={item.path} 
              className="text-sm font-bold text-gray-600 hover:text-suncoast-primary transition-colors"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Desktop CTA & Language */}
        <div className="hidden lg:flex items-center gap-4">
           <button 
             onClick={toggleLanguage} 
             className="text-xs font-bold text-suncoast-primary border border-suncoast-primary/30 px-3 py-2 rounded hover:bg-suncoast-bg transition-colors min-w-[80px]"
           >
             {language === 'en' ? 'ES' : 'EN'}
           </button>
           
           <Link 
            to="/contact" 
            className="px-5 py-2.5 bg-suncoast-accent hover:bg-orange-600 text-white font-bold rounded-full text-sm transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            {t.common.bookPerformanceCheck}
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <div className="lg:hidden flex items-center gap-4">
          <button 
             onClick={toggleLanguage} 
             className="text-xs font-bold text-suncoast-primary border border-suncoast-primary/30 px-2 py-1.5 rounded hover:bg-suncoast-bg"
           >
             {language === 'en' ? 'ES' : 'EN'}
           </button>
          <button 
            className="text-suncoast-primary p-2"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isOpen ? (
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 absolute w-full left-0 shadow-lg z-50 h-[calc(100vh-80px)] overflow-y-auto">
          <div className="flex flex-col p-6 space-y-6">
            {t.nav.map((item) => (
              <Link 
                key={item.path} 
                to={item.path} 
                className="text-xl font-bold text-gray-700 hover:text-suncoast-primary border-b border-gray-50 pb-2"
                onClick={() => setIsOpen(false)}
              >
                {item.label}
              </Link>
            ))}
             <Link 
              to="/contact" 
              onClick={() => setIsOpen(false)}
              className="mt-4 px-5 py-4 bg-suncoast-accent text-white font-bold rounded-lg text-center text-lg shadow-md"
            >
              {t.common.bookPerformanceCheck}
            </Link>
            
             <button 
             onClick={() => { toggleLanguage(); setIsOpen(false); }} 
             className="text-left font-bold text-suncoast-primary mt-4"
            >
              {language === 'en' ? 'Switch to Español' : 'Cambiar a English'}
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => {
  const { language } = useLanguage();
  const t = CONTENT[language];
  const fb = t.common.footerBottom;

  return (
    <footer className="bg-suncoast-primary text-white pt-16 pb-8">
      <div className="container mx-auto px-4 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
          <div>
             <div className="mb-4">
               <div className="flex flex-col">
                 <div className="flex flex-wrap items-baseline leading-none mb-2">
                   <span className="text-lg font-extrabold text-white tracking-tight">SUNCOAST</span>
                   <span className="text-lg font-extrabold text-suncoast-accent ml-1">MD</span>
                   <span className="text-lg font-extrabold text-white ml-1">PARTNERS</span>
                 </div>
                 <div className="flex flex-col gap-1">
                   <span className="text-[10px] font-bold text-suncoast-accent tracking-wide uppercase">Local Support.</span>
                   <span className="text-[10px] font-bold text-suncoast-accent tracking-wide uppercase">Multi-Carrier Experience.</span>
                 </div>
               </div>
             </div>
             <p className="text-suncoast-bg text-sm leading-relaxed mb-4 opacity-90">
               {t.common.footerDesc}
             </p>
             <div className="text-sm text-suncoast-bg opacity-80">
               <p>{t.common.serving}</p>
             </div>
          </div>
          
          <div>
            <h4 className="font-bold mb-4 text-suncoast-bg uppercase tracking-wide text-xs">{t.common.partnership}</h4>
            <ul className="space-y-2 text-sm text-gray-200">
              <li><Link to="/physicians" className="hover:text-white hover:translate-x-1 transition-transform inline-block">{t.common.links.physicians}</Link></li>
              <li><Link to="/economics" className="hover:text-white hover:translate-x-1 transition-transform inline-block">{t.common.links.economics}</Link></li>
              <li><Link to="/onboarding" className="hover:text-white hover:translate-x-1 transition-transform inline-block">{t.common.links.onboarding}</Link></li>
              <li><Link to="/contact" className="hover:text-white hover:translate-x-1 transition-transform inline-block">{t.common.links.intake}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold mb-4 text-suncoast-bg uppercase tracking-wide text-xs">{t.common.resources}</h4>
            <ul className="space-y-2 text-sm text-gray-200">
              <li><Link to="/reality-check" className="hover:text-white hover:translate-x-1 transition-transform inline-block">{t.common.links.realityCheck}</Link></li>
              <li><Link to="/resources" className="hover:text-white hover:translate-x-1 transition-transform inline-block">{t.common.links.blog}</Link></li>
              <li><a href="#" className="hover:text-white hover:translate-x-1 transition-transform inline-block">{t.common.links.hedis}</a></li>
              <li><Link to="/legal?section=privacy" className="hover:text-white hover:translate-x-1 transition-transform inline-block">{t.common.links.privacy}</Link></li>
            </ul>
          </div>

          <div>
             <h4 className="font-bold mb-4 text-suncoast-bg uppercase tracking-wide text-xs">{t.common.contact}</h4>
             <p className="text-sm text-gray-200 mb-2">info@suncoastmdpartners.com</p>
             <p className="text-sm text-gray-200 mb-4">561-388-1805</p>
             <Link to="/contact" className="inline-block px-4 py-2 bg-white text-suncoast-primary font-bold text-sm rounded hover:bg-gray-100 transition-colors">
               {t.common.scheduleCall}
             </Link>
          </div>
        </div>
        
        <div className="border-t border-suncoast-secondary pt-8 text-xs text-suncoast-bg/70">
          <p className="mb-6 leading-relaxed">{t.common.disclaimer}</p>
          
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
             <p>&copy; {new Date().getFullYear()} {t.common.rights}</p>
             <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-suncoast-bg/90">
               <Link to="/legal?section=terms" className="hover:text-white transition-colors">{fb.terms}</Link>
               <Link to="/legal?section=privacy" className="hover:text-white transition-colors">{fb.privacy}</Link>
               <Link to="/legal?section=security" className="hover:text-white transition-colors">{fb.security}</Link>
               <Link to="/legal?section=cookies" className="hover:text-white transition-colors">{fb.cookies}</Link>
               <Link to="/legal?section=donotshare" className="hover:text-white transition-colors">{fb.doNotShare}</Link>
             </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Helper to access context inside the return statement of App
const LanguageConsumer: React.FC<{ children: (context: any) => React.ReactNode }> = ({ children }) => {
  const context = useLanguage();
  return <>{children(context)}</>;
}

export default function App() {
  const [isVoiceModalOpen, setIsVoiceModalOpen] = React.useState(false);

  return (
    <LanguageProvider>
      <Router>
        <ScrollToTop />
        <div className="min-h-screen flex flex-col font-sans text-suncoast-dark bg-white">
          <Header />
          
          <main className="flex-grow">
            <Routes>
              <Route path="/" element={<Home onOpenVoice={() => setIsVoiceModalOpen(true)} />} />
              <Route path="/physicians" element={<Physicians />} />
              <Route path="/economics" element={<Economics />} />
              <Route path="/reality-check" element={<VBCRealityCheck />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/resources" element={<Resources />} />
              <Route path="/legal" element={<Legal />} />
            </Routes>
          </main>

          <CuriousStrip />
          <Footer />
          
          {/* Floating Voice AI Button */}
          <LanguageConsumer>
            {({language}) => {
                const t = CONTENT[language];
                return (
                  <button
                    onClick={() => setIsVoiceModalOpen(true)}
                    className="fixed bottom-6 right-6 z-40 flex items-center gap-2 bg-gradient-to-r from-suncoast-primary to-teal-700 text-white pl-4 pr-5 py-3 rounded-full shadow-2xl hover:shadow-suncoast-primary/40 transition-all hover:scale-105 border-2 border-white/20 group"
                  >
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                    </span>
                    <span className="font-semibold text-sm group-hover:text-white">{t.common.talkToAI}</span>
                  </button>
                )
            }}
          </LanguageConsumer>

          <VoiceAIModal isOpen={isVoiceModalOpen} onClose={() => setIsVoiceModalOpen(false)} />
        </div>
      </Router>
    </LanguageProvider>
  );
}
